import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Container } from '@/components/ui/container';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast'; // Updated import path
import { Helmet } from 'react-helmet';
import { supabase } from '@/integrations/supabase/client';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

const AccountActivated = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const processConfirmation = async () => {
      try {
        setIsProcessing(true);

        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        const queryParams = new URLSearchParams(window.location.search);

        const pkceCode = hashParams.get('code') || queryParams.get('code');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const token = queryParams.get('token');
        const type = queryParams.get('type');

        // Fall 1: PKCE OAuth Flow
        if (pkceCode) {
          const { error } = await supabase.auth.exchangeCodeForSession(pkceCode);
          if (error) throw error;
        }
        // Fall 2: Klassischer access_token im Hash
        else if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
        }
        // Fall 3: E-Mail Bestätigung via Token + Type
        else if (token && type) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: type as 'signup' | 'email' | 'recovery' | 'magiclink',
          });
          if (error) throw error;
        } else {
          throw new Error("Es wurde kein gültiger Code oder Token gefunden.");
        }

        setIsSuccess(true);
        toast({
          title: "Llogaria u aktivizua me sukses",
          description: "Email-i juaj u konfirmua. Ju mund të vazhdoni në faqen kryesore ose në profilin tuaj.",
        });

        window.history.replaceState({}, document.title, window.location.pathname); // clean URL
      } catch (error: any) {
        setIsSuccess(false);
        toast({
          title: "Gabim gjatë aktivizimit të llogarisë",
          description: error?.message || "Ka ndodhur një gabim i panjohur.",
          variant: "destructive"
        });
      } finally {
        setIsProcessing(false);
      }
    };

    processConfirmation();
  }, [toast]);

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Llogaria u Aktivizua | Tregu.al</title>
        <meta name="description" content="Llogaria juaj në Tregu.al u aktivizua me sukses" />
      </Helmet>

      <Navbar />

      <main className="flex-grow pt-16 pb-12 bg-gray-50">
        <Container>
          <div className="max-w-md mx-auto mt-8">
            <Card>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold">
                  {isProcessing ? "Duke aktivizuar llogarinë..." :
                    isSuccess ? "Llogaria u Aktivizua!" : "Aktivizimi dështoi"}
                </CardTitle>
                <CardDescription>
                  {isProcessing ? "Ju lutemi prisni..." :
                    isSuccess ? "Urime! Llogaria juaj tani është aktive. Ju mund të vazhdoni në faqen kryesore." :
                      "Ka ndodhur një problem gjatë aktivizimit."}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {isProcessing ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : isSuccess ? (
                  <div className="text-center py-4">
                    <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-600 mt-2">
                      Email-i juaj u verifikua me sukses.
                    </p>
                    <Button onClick={() => navigate('/')} className="mt-4 w-full bg-brand-500 hover:bg-brand-600">
                      Shko tek Faqja Kryesore
                    </Button>
                    <Button variant="link" onClick={() => navigate('/profile')} className="mt-2 text-brand-500">
                      Ose shko tek Profili
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <p className="text-gray-600 mt-2">
                      Verifikimi dështoi. Ju lutemi provoni përsëri ose kontaktoni mbështetjen.
                    </p>
                    <Button onClick={() => navigate('/auth')} className="mt-4 w-full bg-brand-500 hover:bg-brand-600">
                      Provo përsëri
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export default AccountActivated;
